import { useState, useEffect, useMemo } from 'react';
import StatisticsAPI, { StatisticsApiResponse, StatisticsFilters } from '../api/StatisticsAPI';

export type StatisticsType = 'total-year' | 'total-month' | 'total-shop' | 'total-visits' | 'orders-total-per-year' | 'orders-monthly-grouped-by-year' | 'orders-monthly-by-shop-grouped-by-year' | 'total-sales-by-shop-grouped-by-year' | 'active-orders-grouped-by-status' | 'sold-dresses-per-year' | 'store-top-selling-items' | 'store-income-over-time' | 'store-top-bookmarked-items' | 'store-top-viewed-items' | 'store-visitor-sources';

const StatisticsTypeToApiMapping: Record<StatisticsType, (filters?: StatisticsFilters) => Promise<{data: StatisticsApiResponse}>> = {
  'total-year': StatisticsAPI.getMostPopularDresses,
  'total-month': StatisticsAPI.getWeddingMonthSummary,
  'total-shop': StatisticsAPI.getByShops,
  'total-visits': StatisticsAPI.getVisitors,
  'orders-total-per-year': StatisticsAPI.getTotalOrdersPerYear,
  'orders-monthly-grouped-by-year': StatisticsAPI.getMonthlyOrdersGroupedByYear,
  'orders-monthly-by-shop-grouped-by-year': StatisticsAPI.getMonthlyOrdersByShopGroupedByYear,
  'total-sales-by-shop-grouped-by-year': StatisticsAPI.getTotalSalesByShopGroupedByYear,
  'active-orders-grouped-by-status': StatisticsAPI.getActiveOrdersGroupedByStatus,
  'sold-dresses-per-year': StatisticsAPI.getTotalSalesByDressGroupedByYear,
  'store-top-selling-items': StatisticsAPI.getTopSellingItems,
  'store-income-over-time': StatisticsAPI.getIncomeOverTime,
  'store-top-bookmarked-items': StatisticsAPI.getTopBookmarkedItems,
  'store-top-viewed-items': StatisticsAPI.getTopViewedItems,
  'store-visitor-sources': StatisticsAPI.getVisitorSources
};

// Mock data for eCommerce statistics
const getMockTopSellingItems = (): StatisticsApiResponse => {
  return {
    'Elegant A-Line Wedding Dress': 45,
    'Classic Ball Gown': 38,
    'Boho Chic Lace Dress': 32,
    'Modern Minimalist Gown': 28,
    'Vintage Inspired Dress': 25,
    'Princess Style Wedding Dress': 22,
    'Mermaid Silhouette Gown': 20,
    'Romantic Floral Dress': 18,
    'Simple Elegant Dress': 15,
    'Luxury Designer Gown': 12,
    'Bridal Jumpsuit': 10,
    'Two-Piece Wedding Set': 8,
  } as unknown as StatisticsApiResponse;
};

const getMockIncomeOverTime = (filters?: StatisticsFilters): StatisticsApiResponse => {
  // Generate daily data for the last 30 days or based on filters
  const startDate = filters?.startTime ? new Date(filters.startTime) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const endDate = filters?.endTime ? new Date(filters.endTime) : new Date();
  
  const data: any = {};
  const currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    const dateKey = currentDate.toISOString().split('T')[0];
    // Generate realistic income with some variation
    const baseIncome = 1200 + Math.random() * 800;
    data[dateKey] = Math.round(baseIncome * 100) / 100;
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return data as unknown as StatisticsApiResponse;
};

const getMockTopBookmarkedItems = (): StatisticsApiResponse => {
  return {
    'Elegant A-Line Wedding Dress': 156,
    'Boho Chic Lace Dress': 142,
    'Modern Minimalist Gown': 128,
    'Classic Ball Gown': 115,
    'Vintage Inspired Dress': 98,
    'Romantic Floral Dress': 87,
    'Princess Style Wedding Dress': 76,
    'Mermaid Silhouette Gown': 65,
    'Luxury Designer Gown': 54,
    'Simple Elegant Dress': 43,
    'Bridal Jumpsuit': 32,
    'Two-Piece Wedding Set': 21,
  } as unknown as StatisticsApiResponse;
};

const getMockTopViewedItems = (): StatisticsApiResponse => {
  return {
    'Elegant A-Line Wedding Dress': 1245,
    'Classic Ball Gown': 1123,
    'Boho Chic Lace Dress': 987,
    'Modern Minimalist Gown': 856,
    'Vintage Inspired Dress': 743,
    'Princess Style Wedding Dress': 632,
    'Mermaid Silhouette Gown': 521,
    'Romantic Floral Dress': 445,
    'Luxury Designer Gown': 367,
    'Simple Elegant Dress': 289,
    'Bridal Jumpsuit': 198,
    'Two-Piece Wedding Set': 134,
  } as unknown as StatisticsApiResponse;
};

const getMockVisitorSources = (): StatisticsApiResponse => {
  return {
    'Instagram': 1245,
    'Direct': 856,
    'Google': 632,
    'Facebook': 445,
    'Pinterest': 289,
    'TikTok': 198,
    'instagram.com/reel/abc123': 156,
    'facebook.com/page/xyz': 134,
    'google.com/search?q=wedding+dress': 98,
    'pinterest.com/pin/123': 76,
    'tiktok.com/@bridal/video/456': 54,
    'Other': 43,
  } as unknown as StatisticsApiResponse;
};

export function useStatistics(statisticsType: StatisticsType, filters?: StatisticsFilters) {
  const [statistics, setStatistics] = useState<StatisticsApiResponse>({});

  const memoizedFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      // Return mock data for eCommerce statistics
      if (statisticsType === 'store-top-selling-items') {
        await new Promise(resolve => setTimeout(resolve, 300));
        setStatistics(getMockTopSellingItems());
        return;
      }
      
      if (statisticsType === 'store-income-over-time') {
        await new Promise(resolve => setTimeout(resolve, 300));
        setStatistics(getMockIncomeOverTime(memoizedFilters));
        return;
      }
      
      if (statisticsType === 'store-top-bookmarked-items') {
        await new Promise(resolve => setTimeout(resolve, 300));
        setStatistics(getMockTopBookmarkedItems());
        return;
      }
      
      if (statisticsType === 'store-top-viewed-items') {
        await new Promise(resolve => setTimeout(resolve, 300));
        setStatistics(getMockTopViewedItems());
        return;
      }
      
      if (statisticsType === 'store-visitor-sources') {
        await new Promise(resolve => setTimeout(resolve, 300));
        setStatistics(getMockVisitorSources());
        return;
      }

      // For other statistics types, use the API
      const apiMethod = StatisticsTypeToApiMapping[statisticsType];

      if (!apiMethod) {
        throw new Error('Invalid statistics type');
      }

      try {
        const response = await apiMethod(memoizedFilters);
        setStatistics(response.data);
      } catch (error) {
        console.error('Error fetching statistics data:', error);
      }
    };

    fetchData();
  }, [statisticsType, memoizedFilters]);

  return statistics;
}
